import { runSimpleWeightedScoringTool } from '../../../tools/simpleWeightedScoring';
import type { CriteriaSet } from '../../../harmonizer/types';
import { type ScoringItem, type Criterion, type ScoreMatrix, type WeightedResult, type ScoringScale } from '../types';

// NOTE: the weighted-total math lives in the canonical engine adapter
// (src/tools/simpleWeightedScoring.ts) — this module only maps the page's
// state into the engine's contract and back. Weight-input helpers
// (validateWeights / normalizeWeights / isScoringComplete) stay page-local
// because they gate the UI, not the scoring.

/**
 * Score all items via the engine adapter. Returns results sorted by weighted
 * total (descending, label tie-break — the engine's ordering).
 */
export async function runWeightedScoring(
  items: ScoringItem[],
  criteria: Criterion[],
  scoreMatrix: ScoreMatrix,
  scale: ScoringScale
): Promise<WeightedResult[]> {
  const criteriaSet: CriteriaSet = {
    id: 'simple-weighted-scoring-page',
    name: 'Weighted Scoring Criteria',
    // The engine reads an optional `weight` extension on each criterion.
    criteria: criteria.map((c) => ({
      id: c.id,
      name: c.name,
      type: 'numeric' as const,
      weight: c.weight,
    })),
  };

  const scoreSet = await runSimpleWeightedScoringTool(
    items.map((item) => ({
      id: item.id,
      label: item.name,
      attributes: Object.fromEntries(
        criteria.map((c) => [c.id, scoreMatrix[item.id]?.[c.id] ?? 0])
      ),
    })),
    criteriaSet,
    {
      scoring_scale_min: scale.min,
      scoring_scale_max: scale.max,
      require_all_scores: false,
    }
  );

  return scoreSet.items.map((s) => {
    const contributions = (s.criterion_contributions ?? {}) as Record<
      string,
      { score: number; weighted_contribution: number }
    >;
    const criterionScores: WeightedResult['criterionScores'] = {};
    for (const criterion of criteria) {
      const c = contributions[criterion.id];
      criterionScores[criterion.id] = c
        ? {
            score: c.score,
            weightedContribution: parseFloat(c.weighted_contribution.toFixed(3)),
          }
        : { score: scoreMatrix[s.item_id as string]?.[criterion.id] ?? 0, weightedContribution: 0 };
    }

    return {
      itemId: s.item_id as string,
      itemName: items.find((i) => i.id === s.item_id)?.name || (s.item_id as string),
      totalScore: parseFloat((s.weighted_total as number).toFixed(3)),
      criterionScores,
    };
  });
}

/**
 * Check if weights sum to 100%
 */
export function validateWeights(criteria: Criterion[]): {
  isValid: boolean;
  total: number;
  message: string;
} {
  const total = criteria.reduce((sum, c) => sum + c.weight, 0);
  const isValid = Math.abs(total - 100) < 0.01; // Allow small floating point errors

  return {
    isValid,
    total: parseFloat(total.toFixed(2)),
    message: isValid
      ? 'Weights sum to 100%'
      : `Weights sum to ${total.toFixed(2)}%. They must sum to exactly 100%.`,
  };
}

/**
 * Normalize weights to sum to 100%
 */
export function normalizeWeights(criteria: Criterion[]): Criterion[] {
  const total = criteria.reduce((sum, c) => sum + c.weight, 0);
  if (total === 0) return criteria;

  const factor = 100 / total;
  return criteria.map((c) => ({
    ...c,
    weight: parseFloat((c.weight * factor).toFixed(2)),
  }));
}

/**
 * Check if all scores are filled
 */
export function isScoringComplete(
  items: ScoringItem[],
  criteria: Criterion[],
  scoreMatrix: ScoreMatrix
): boolean {
  return items.every((item) =>
    criteria.every((criterion) => {
      const score = scoreMatrix[item.id]?.[criterion.id];
      return score !== undefined && score !== null;
    })
  );
}

/**
 * Get RICE criteria template
 */
export function getRICECriteria(): Omit<Criterion, 'id' | 'weight'>[] {
  return [
    {
      name: 'Reach',
      description: 'How many people will this affect? (1 = few, 5 = many)',
    },
    {
      name: 'Impact',
      description: 'How much will this impact each person? (1 = low, 5 = high)',
    },
    {
      name: 'Confidence',
      description: 'How confident are we in our estimates? (1 = low, 5 = high)',
    },
    {
      name: 'Effort',
      description: 'How much work is required? (1 = low effort, 5 = high effort)',
    },
  ];
}
